import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { TopBar } from '../components/layout/TopBar';
import { dossiers, getDossier } from '../data/mockData';
import { productDossiers } from '../data/productDossiers';
import { getProductRecord } from '../data/productRecords';
import { NipTab } from '../components/dossier/NipTab';
import { FoodFormulaTab } from '../components/dossier/FoodFormulaTab';
import { FoodSuggestedClaimsTab } from '../components/dossier/FoodSuggestedClaimsTab';
import { FoodPackagingLabelTab } from '../components/dossier/FoodPackagingLabelTab';
import { FoodClaimsTab } from '../components/dossier/FoodClaimsTab';
import { OverviewTab } from '../components/dossier/OverviewTab';
import { FormulaTab } from '../components/dossier/FormulaTab';
import { SgFormulaTab } from '../components/dossier/SgFormulaTab';
import { ClaimsTab } from '../components/dossier/ClaimsTab';
import { SgClaimsTab } from '../components/dossier/SgClaimsTab';
import { ProductDossierTab } from '../components/dossier/ProductDossierTab';
import { ItalyDossierTab } from '../components/dossier/ItalyDossierTab';
import { ItalyFormulaTab } from '../components/dossier/ItalyFormulaTab';
import { ItalyClaimsTab } from '../components/dossier/ItalyClaimsTab';
import { ItalySuggestedClaimsTab } from '../components/dossier/ItalySuggestedClaimsTab';
import { PhLabelTab } from '../components/dossier/PhLabelTab';
import { PhFormulaTab } from '../components/dossier/PhFormulaTab';
import { PhClaimsTab } from '../components/dossier/PhClaimsTab';
import { PhDossierTab } from '../components/dossier/PhDossierTab';
import { PhNipTab } from '../components/dossier/PhNipTab';
import { PhSuggestedClaimsTab } from '../components/dossier/PhSuggestedClaimsTab';
import { LabelUploadEmptyState } from '../components/dossier/LabelUploadEmptyState';
import { PackagingLabelTab } from '../components/dossier/PackagingLabelTab';
import { SuggestedClaimsTab } from '../components/dossier/SuggestedClaimsTab';
import { ProductRecordShell, ProductRecordTabId } from '../components/dossier/ProductRecordShell';
type TabId = ProductRecordTabId;
export function DossierDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const listedProduct = productDossiers.find(product => product.id === id);
  const record = getProductRecord(id);
  const dossier = id ? getDossier(id) ?? (listedProduct ? {
    ...dossiers[0],
    id: listedProduct.id,
    name: listedProduct.recordName ?? listedProduct.name,
    readiness: listedProduct.readiness,
    primaryMarket: listedProduct.jurisdictions[0]
  } : undefined) : undefined;
  const [tab, setTab] = useState<TabId>('overview');
  const [market, setMarket] = useState(record.market);
  const [coaUploaded, setCoaUploaded] = useState(false);
  const [gmpExpired, setGmpExpired] = useState(false);
  const [coaWrongProduct, setCoaWrongProduct] = useState(false);
  const isSingapore = market.code === 'SG';
  const isItaly = market.code === 'IT';
  const isPhilippines = market.code === 'PH';
  const hasNoLabel = id === 'card-complex-ven' || !['orange-immunity-powder', 'chocolate-powdered-drink', 'adult-snacks-protein'].includes(id ?? '');
  const marketChips = id === 'orange-immunity-powder' ? [{
    code: 'AU',
    flag: '🇦🇺',
    score: 45
  }, {
    code: 'SG',
    flag: '🇸🇬',
    score: 58
  }] : id === 'chocolate-powdered-drink' ? [{
    code: 'AU',
    flag: '🇦🇺',
    score: 0
  }] : id === 'card-complex-ven' ? [{
    code: 'IT',
    flag: '🇮🇹',
    score: 24
  }] : id === 'adult-snacks-protein' ? [{
    code: 'PH',
    flag: '🇵🇭',
    score: 32
  }] : [{
    code: 'AU',
    flag: '🇦🇺',
    score: 22
  }, {
    code: 'SG',
    flag: '🇸🇬',
    locked: true
  }, {
    code: 'US',
    flag: '🇺🇸',
    locked: true
  }];
  const activeOverview = isSingapore && record.sgOverview ? record.sgOverview : record.overview;
  const activeReadiness = isSingapore && record.sgReadiness !== undefined ? record.sgReadiness : undefined;
  if (!dossier) {
    return <>
        <TopBar title="Dossier not found" data-id="element-2686" data-mp="2kbteym62xnoj" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-id="element-2687" data-mp="2kaifuznkx43r">
          <p className="text-sm text-slate-500" data-id="element-2688" data-mp="2fbf99aryapto">We couldn't find that product.</p>
          <button type="button" onClick={() => navigate('/dossiers')} className="rounded-lg bg-evergreen px-4 py-2 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-2689" data-mp="3c393ilr75khe">
            Back to Dossiers
          </button>
        </div>
      </>;
  }
  return <>
      <TopBar title={<button type="button" onClick={() => navigate('/dossiers')} aria-label="Back to product dossiers" className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-evergreen" data-id="element-2691" data-mp="1ro7f9ozz336y">
            <ArrowLeft className="h-5 w-5" data-id="element-2692" data-mp="1hksbn3f8wemg" />
            Product dossiers
          </button>} data-id="element-2690" data-mp="2y6domco9kytf" />

      <ProductRecordShell dossier={activeReadiness !== undefined ? {
      ...dossier,
      readiness: activeReadiness
    } : dossier} record={record} market={market} markets={isItaly || isPhilippines ? [record.market] : [record.market, {
      code: 'SG',
      flag: '🇸🇬',
      name: 'Singapore',
      authority: record.hasNip ? 'SFA' : 'HSA'
    }]} marketChips={marketChips} onMarketChange={setMarket} activeTab={tab} onTabChange={setTab} data-id="element-2693" data-mp="1mhirpqr1va7v">
        {tab === 'overview' && <OverviewTab dossier={activeReadiness !== undefined ? {
        ...dossier,
        readiness: activeReadiness
      } : dossier} overview={activeOverview} onFormulaClick={() => setTab('formula')} onClaimsClick={() => setTab('claims')} onLabelClick={() => setTab('label')} onBackClick={() => navigate('/dossiers')} marketCode={market.code} marketFlag={market.flag} extraNeed={[...(coaWrongProduct ? [{
        title: 'Finished Product CoA — wrong document uploaded, replace with the correct certificate',
        status: 'Blocking' as const,
        category: 'Documentation'
      }] : []), ...(gmpExpired ? [{
        title: 'GMP certificate expired — replace or renew before filing',
        status: 'Blocking' as const,
        category: 'Documentation'
      }] : []), ...(coaUploaded ? [{
        title: 'Finished Product CoA expired — replace with a current certificate',
        status: 'To action' as const,
        category: 'Documentation'
      }] : [])]} data-id="element-2694" data-mp="2a7wkxkldff41" />}
        {tab === 'dossier' && (isPhilippines ? <PhDossierTab onScanLabel={() => setTab('label')} onGmpDetected={() => setGmpExpired(true)} onCoaWrongProduct={setCoaWrongProduct} data-id="element-2695" data-mp="1f4a54idfr9ah" /> : isItaly ? <ItalyDossierTab onScanLabel={() => setTab('label')} data-id="element-2696" data-mp="15dyxakqmsfyb" /> : <ProductDossierTab productName={record.hasNip ? 'Chocolate Powdered Drink' : isPhilippines ? 'Cheese Snack' : 'Orange Immunity Powder'} isSingapore={isSingapore} onCoaUploaded={() => setCoaUploaded(true)} onScanLabel={() => setTab('label')} data-id="element-2697" data-mp="1a70jldt63hrx" />)}
        {tab === 'formula' && (isPhilippines ? <PhFormulaTab onViewLabel={() => setTab('label')} data-id="element-2698" data-mp="rgadzhc7pgvd" /> : isItaly ? <ItalyFormulaTab data-id="element-2699" data-mp="rhlijjdhesnj" /> : record.hasNip ? <FoodFormulaTab data-id="element-2700" data-mp="16ya9ja5vu0u4" /> : isSingapore ? <SgFormulaTab data-id="element-2701" data-mp="16x8qaiepq81b" /> : <FormulaTab dossier={dossier} data-id="element-2702" data-mp="16vo53jeag1g4" />)}
        {tab === 'nip' && (isPhilippines ? <PhNipTab onViewLabel={() => setTab('label')} data-id="element-2703" data-mp="11z76twddk6pb" /> : <NipTab data-id="element-2704" data-mp="xwu0p6upu6s0" />)}
        {tab === 'label' && (isPhilippines ? <PhLabelTab data-id="element-2705" data-mp="xyx36qd21sdm" /> : hasNoLabel ? <LabelUploadEmptyState note="No label data has been added to this product yet." onBackClick={() => navigate('/dossiers')} data-id="element-2706" data-mp="sx7o8zmpouk9" /> : record.hasNip ? <FoodPackagingLabelTab data-id="element-2707" data-mp="j7efs7hv8vk8" /> : <PackagingLabelTab market={isSingapore ? 'SG' : 'AU'} onViewFormula={() => setTab('formula')} onBackClick={() => navigate('/dossiers')} data-id="element-2708" data-mp="e8tnqvcrpi6v" />)}
        {tab === 'claims' && (isPhilippines ? <PhClaimsTab data-id="element-2709" data-mp="58ogqmbhklt8" /> : isItaly ? <ItalyClaimsTab data-id="element-2710" data-mp="5a9awatw22qk" /> : record.hasNip ? <FoodClaimsTab data-id="element-2711" data-mp="amy22hz2oia" /> : isSingapore ? <SgClaimsTab data-id="element-2712" data-mp="2bwxgyx9w7u1g" /> : <ClaimsTab onBackClick={() => navigate('/dossiers')} data-id="element-2713" data-mp="2bvvu7vjilj3w" />)}
        {tab === 'suggested-claims' && (isPhilippines ? <PhSuggestedClaimsTab onClaimsClick={() => setTab('claims')} onLabelClick={() => setTab('label')} data-id="element-2714" data-mp="2luv39f86zgrf" /> : isItaly ? <ItalySuggestedClaimsTab data-id="element-2715" data-mp="2pvf2cocbsug7" /> : record.hasNip ? <FoodSuggestedClaimsTab onClaimsClick={() => setTab('claims')} data-id="element-2716" data-mp="1t6fp0uwvx2h5" /> : <SuggestedClaimsTab market={isSingapore ? 'SG' : 'AU'} data-id="element-2717" data-mp="1y50sr4bci4ac" />)}
      </ProductRecordShell>
    </>;
}